import { listActions } from './actions/registry'
import './actions/domains/automation'
import type {
  AutomationActionType,
  AutomationAction,
  AiEventTriggerConfig,
  AutomationCondition,
  AutomationConditionClause,
  AutomationTrigger,
  AutomationTriggerType,
  AutomationTriggerEvent,
  CreateNodeActionConfig,
  NotifyUserActionConfig,
  UpdateFieldActionConfig,
  WebhookActionConfig,
} from '../types/automation'

export type RegistryFieldKind = 'TEXT' | 'TEXTAREA' | 'NUMBER' | 'SELECT' | 'TOGGLE'

export type RegistryField = {
  key: string
  label: string
  kind: RegistryFieldKind
  required?: boolean
  helpText?: string
  options?: Array<{ value: string; label: string }>
  placeholder?: string
}

export type TriggerSchema = {
  type: AutomationTriggerType
  label: string
  description: string
  available: boolean
  unavailableReason?: string
  fields: RegistryField[]
  defaults: Record<string, unknown>
}

export type ActionSchema = {
  type: AutomationActionType
  label: string
  description: string
  external: boolean
  fields: RegistryField[]
  defaults: Record<string, unknown>
  simulatedOutcomeTemplate: string
}

export const TRIGGER_SCHEMAS: Record<AutomationTriggerType, TriggerSchema> = {
  NODE_CREATED: {
    type: 'NODE_CREATED',
    label: 'Node created',
    description: 'Runs when a timeline node is created in this workspace.',
    available: true,
    fields: [
      {
        key: 'nodeKind',
        label: 'Node kind',
        kind: 'SELECT',
        options: [
          { value: 'ANY', label: 'Any kind' },
          { value: 'GOAL', label: 'Goal' },
          { value: 'TASK', label: 'Task' },
          { value: 'SUBTASK', label: 'Subtask' },
        ],
      },
      { key: 'boardKey', label: 'Board (optional)', kind: 'TEXT', placeholder: 'DASHBOARD, VISIONBOARD…' },
    ],
    defaults: { nodeKind: 'ANY', scope: 'WORKSPACE' },
  },
  CHECKLIST_CREATED: {
    type: 'CHECKLIST_CREATED',
    label: 'Checklist created',
    description: 'Runs when a checklist node is created in this workspace.',
    available: true,
    fields: [{ key: 'boardKey', label: 'Board (optional)', kind: 'TEXT', placeholder: 'DASHBOARD, VISIONBOARD...' }],
    defaults: { nodeKind: 'ANY', scope: 'WORKSPACE' },
  },
  CHECKLIST_COMPLETED: {
    type: 'CHECKLIST_COMPLETED',
    label: 'Checklist completed',
    description: 'Runs after the server accepts a checklist completion.',
    available: true,
    fields: [{ key: 'boardKey', label: 'Board (optional)', kind: 'TEXT', placeholder: 'DASHBOARD, VISIONBOARD...' }],
    defaults: { nodeKind: 'ANY', scope: 'WORKSPACE' },
  },
  PROOF_UPDATED: {
    type: 'PROOF_UPDATED',
    label: 'Proof updated',
    description: 'Runs when proof is attached, replaced, or removed.',
    available: true,
    fields: [{ key: 'boardKey', label: 'Board (optional)', kind: 'TEXT', placeholder: 'DASHBOARD, VISIONBOARD...' }],
    defaults: { nodeKind: 'ANY', scope: 'WORKSPACE' },
  },
  TASK_ASSIGNED: {
    type: 'TASK_ASSIGNED',
    label: 'Task assigned',
    description: 'Runs when a task or checklist assignment changes.',
    available: true,
    fields: [{ key: 'boardKey', label: 'Board (optional)', kind: 'TEXT', placeholder: 'DASHBOARD, VISIONBOARD...' }],
    defaults: { nodeKind: 'ANY', scope: 'WORKSPACE' },
  },
  DATE_REACHED: {
    type: 'DATE_REACHED',
    label: 'Date reached',
    description: 'Runs on a cron schedule. Used by recurring-checklist companions.',
    available: true,
    fields: [
      { key: 'cron', label: 'Cron', kind: 'TEXT', placeholder: '0 9 * * 1', helpText: 'Compiled from the linked recurring template — edit there.' },
      { key: 'isoDate', label: 'One-shot ISO date (optional)', kind: 'TEXT', placeholder: '2026-07-01T09:00:00Z' },
      { key: 'endIsoDate', label: 'End date (optional YYYY-MM-DD)', kind: 'TEXT', placeholder: '2026-12-31' },
    ],
    defaults: {},
  },
  FORM_SUBMITTED: {
    type: 'FORM_SUBMITTED',
    label: 'Form submitted',
    description: 'Runs when a workspace form is submitted.',
    available: true,
    fields: [{ key: 'formId', label: 'Form ID', kind: 'TEXT', required: true }],
    defaults: {},
  },
  UPLOAD_COMPLETED: {
    type: 'UPLOAD_COMPLETED',
    label: 'Upload completed',
    description: 'Runs when an upload artifact finishes.',
    available: true,
    fields: [
      {
        key: 'providerId',
        label: 'Storage provider',
        kind: 'SELECT',
        options: [
          { value: 'ANY', label: 'Any provider' },
          { value: 'GOOGLE_DRIVE', label: 'Google Drive' },
          { value: 'DROPBOX', label: 'Dropbox' },
          { value: 'NOTION', label: 'Notion' },
        ],
      },
    ],
    defaults: { providerId: 'ANY' },
  },
  NODE_COMPLETED: {
    type: 'NODE_COMPLETED',
    label: 'Node completed',
    description: 'Runs when a goal, task, subtask, or checklist reaches 100%.',
    available: true,
    fields: [
      {
        key: 'nodeKind',
        label: 'Node kind',
        kind: 'SELECT',
        options: [
          { value: 'ANY', label: 'Any kind' },
          { value: 'GOAL', label: 'Goal' },
          { value: 'TASK', label: 'Task' },
          { value: 'SUBTASK', label: 'Subtask' },
          { value: 'CHECKLIST', label: 'Checklist' },
        ],
      },
      { key: 'boardKey', label: 'Board (optional)', kind: 'TEXT' },
    ],
    defaults: { nodeKind: 'ANY' },
  },
  AI_ESSAY_GENERATED: {
    type: 'AI_ESSAY_GENERATED',
    label: 'AI essay generated',
    description: 'Runs after the AI Generator ships a fresh essay draft.',
    available: true,
    fields: [],
    defaults: {},
  },
  AI_REFINE_USED: {
    type: 'AI_REFINE_USED',
    label: 'AI refine used',
    description: 'Runs after an AI refine pass completes.',
    available: true,
    fields: [],
    defaults: {},
  },
  FOCUS_BLOCK_COMPLETED: {
    type: 'FOCUS_BLOCK_COMPLETED',
    label: 'Focus block completed',
    description: 'Runs when a Focus Horizon session finishes.',
    available: true,
    fields: [
      { key: 'minMinutes', label: 'Minimum minutes (optional)', kind: 'NUMBER', placeholder: '25' },
    ],
    defaults: {},
  },
  SPIN_WON: {
    type: 'SPIN_WON',
    label: 'Spin prize won',
    description: 'Runs when a Lucky / Mega / Giga spin resolves with a prize.',
    available: true,
    fields: [
      {
        key: 'tier',
        label: 'Spin tier',
        kind: 'SELECT',
        options: [
          { value: 'ANY', label: 'Any tier' },
          { value: 'LUCKY_SPIN', label: 'Lucky' },
          { value: 'MEGA_SPIN', label: 'Mega' },
          { value: 'GIGA_SPIN', label: 'Giga' },
        ],
      },
    ],
    defaults: { tier: 'ANY' },
  },
  CHAT_MESSAGE_POSTED: {
    type: 'CHAT_MESSAGE_POSTED',
    label: 'Chat message posted',
    description: 'Runs when a group-chat message is sent in this workspace.',
    available: true,
    fields: [
      { key: 'channelId', label: 'Channel ID (optional)', kind: 'TEXT' },
      { key: 'mentionsOnly', label: 'Only when someone is mentioned', kind: 'TOGGLE' },
    ],
    defaults: {},
  },
}

const automationActionSchemas = (): Record<AutomationActionType, ActionSchema> => Object.fromEntries(
  listActions({ surface: 'automate' })
    .filter((action) => action.automate)
    .map((action) => [action.automate!.actionType, {
      type: action.automate!.actionType,
      label: action.label,
      description: action.description,
      external: Boolean(action.automate!.external),
      fields: action.automate!.fields,
      defaults: action.automate!.defaults,
      simulatedOutcomeTemplate: action.automate!.simulatedOutcomeTemplate,
    } satisfies ActionSchema]),
) as Record<AutomationActionType, ActionSchema>

const getActionSchema = (type: AutomationActionType): ActionSchema => {
  const schema = automationActionSchemas()[type]
  if (!schema) throw new Error(`Unknown automation action type: ${type}`)
  return schema
}

export const ACTION_SCHEMAS = new Proxy({} as Record<AutomationActionType, ActionSchema>, {
  get: (_target, property: string | symbol) => {
    if (typeof property !== 'string') return undefined
    return automationActionSchemas()[property as AutomationActionType]
  },
  has: (_target, property: string | symbol) => typeof property === 'string' && Boolean(automationActionSchemas()[property as AutomationActionType]),
  ownKeys: () => Object.keys(automationActionSchemas()),
  getOwnPropertyDescriptor: (_target, property: string | symbol) => {
    if (typeof property !== 'string') return undefined
    return automationActionSchemas()[property as AutomationActionType]
      ? { enumerable: true, configurable: true }
      : undefined
  },
})


export const isExternalAction = (type: AutomationActionType) => getActionSchema(type).external

export const automationHasExternalAction = (actions: AutomationAction[]) =>
  actions.some((action) => isExternalAction(action.type))

const interpolate = (template: string, payload: Record<string, unknown>) =>
  template.replace(/\{(\w+)\}/g, (_, key: string) => {
    const value = payload[key]
    if (value === undefined || value === null || value === '') return `«${key}»`
    return String(value)
  })

export const buildSimulatedOutcome = (action: AutomationAction): string => {
  const schema = getActionSchema(action.type)
  const payload: Record<string, unknown> = { ...action.config }
  if (action.type === 'NOTIFY_USER') {
    const cfg = action.config as NotifyUserActionConfig
    payload.recipient = cfg.recipientUserId ?? cfg.recipientRole ?? 'workspace admins'
  }
  return interpolate(schema.simulatedOutcomeTemplate, payload)
}

export const validateAction = (action: AutomationAction): string | null => {
  const schema = getActionSchema(action.type)
  const cfg = action.config as Record<string, unknown>
  for (const field of schema.fields) {
    if (field.required && (cfg[field.key] === undefined || cfg[field.key] === '' || cfg[field.key] === null)) {
      return `${schema.label}: ${field.label} is required.`
    }
  }
  if (action.type === 'WEBHOOK') {
    const config = action.config as WebhookActionConfig
    if (!['G_DRIVE', 'NOTION', 'JIRA', 'GENERIC_WEBHOOK'].includes(config.providerId)) return 'External webhook: unsupported provider.'
    if (config.providerId === 'GENERIC_WEBHOOK' && !config.connectionId?.trim()) return 'External webhook: Connection ID is required for generic webhooks.'
  }
  return null
}

export const validateTrigger = (trigger: AutomationTrigger): string | null => {
  const schema = TRIGGER_SCHEMAS[trigger.type]
  if (!schema.available) return schema.unavailableReason ?? `${schema.label} is unavailable in this phase.`
  const cfg = trigger.config as Record<string, unknown>
  for (const field of schema.fields) {
    if (field.required && (cfg[field.key] === undefined || cfg[field.key] === '' || cfg[field.key] === null)) {
      return `${schema.label}: ${field.label} is required.`
    }
  }
  return null
}

export const createDefaultActionConfig = (type: AutomationActionType): AutomationAction['config'] => {
  const defaults = { ...getActionSchema(type).defaults }
  switch (type) {
    case 'CREATE_NODE':
      return { title: '', nodeKind: 'TASK', ...defaults } as CreateNodeActionConfig
    case 'NOTIFY_USER':
      return { title: 'Automation fired', ...defaults } as NotifyUserActionConfig
    case 'UPDATE_FIELD':
      return { targetType: 'NODE', fieldPath: '', value: '', ...defaults } as UpdateFieldActionConfig
    case 'WEBHOOK':
      return { providerId: 'GENERIC_WEBHOOK', connectionId: '', method: 'POST', ...defaults } as WebhookActionConfig
    // v2 actions — treated as loose Record configs by the orchestrator.
    // TS-side cast keeps the discriminated union happy; runners read the
    // fields off the raw record.
    case 'COMPLETE_NODE':
    case 'ASSIGN_TASK':
    case 'CREATE_CHECKLIST_ITEM':
    case 'SEND_CHAT_MESSAGE':
    case 'GRANT_CREDITS':
    case 'DEDUCT_CREDITS':
    case 'AWARD_XP':
    case 'DELAY':
    case 'SET_VARIABLE':
    case 'LOG_TO_LEDGER':
    case 'ADVANCE_BOOK_WINDOW':
      return { ...defaults } as unknown as AutomationAction['config']
  }
}

export const createDefaultTrigger = (type: AutomationTriggerType): AutomationTrigger => {
  const defaults = { ...TRIGGER_SCHEMAS[type].defaults }
  switch (type) {
    case 'NODE_CREATED':
      return { type, config: { scope: 'WORKSPACE', nodeKind: 'ANY', ...defaults } }
    case 'CHECKLIST_CREATED':
    case 'CHECKLIST_COMPLETED':
    case 'PROOF_UPDATED':
    case 'TASK_ASSIGNED':
      return { type, config: { scope: 'WORKSPACE', nodeKind: 'ANY', ...defaults } }
    case 'DATE_REACHED':
      return { type, config: { ...defaults } }
    case 'FORM_SUBMITTED':
      return { type, config: { ...defaults } }
    case 'UPLOAD_COMPLETED':
      return { type, config: { providerId: 'ANY', ...defaults } }
    case 'NODE_COMPLETED':
      return { type, config: { scope: 'WORKSPACE', nodeKind: 'ANY', ...defaults } }
    case 'AI_ESSAY_GENERATED':
    case 'AI_REFINE_USED':
      return { type, config: { ...defaults } as AiEventTriggerConfig }
    case 'FOCUS_BLOCK_COMPLETED':
      return { type, config: { ...defaults } }
    case 'SPIN_WON':
      return { type, config: { tier: 'ANY', ...defaults } }
    case 'CHAT_MESSAGE_POSTED':
      return { type, config: { ...defaults } }
  }
}

// ---------------------------------------------------------------------------
// v2 helpers — pure evaluators for conditions, constraints, and trigger match.
// Kept firebase-free so registry stays testable.
// ---------------------------------------------------------------------------

export type AutomationRunContext = {
  trigger: { type: AutomationTriggerType }
  payload: Record<string, unknown>
  actor: { id: string; role?: string; email?: string }
  vars: Record<string, unknown>
}

const readPath = (ctx: AutomationRunContext, path: string): unknown => {
  const segments = path.split('.').filter(Boolean)
  let cursor: unknown = ctx
  for (const seg of segments) {
    if (cursor === null || cursor === undefined || typeof cursor !== 'object') return undefined
    cursor = (cursor as Record<string, unknown>)[seg]
  }
  return cursor
}

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string' && value.trim() !== '' && !Number.isNaN(Number(value))) return Number(value)
  return null
}

const evalClause = (ctx: AutomationRunContext, clause: AutomationConditionClause): boolean => {
  const left = readPath(ctx, clause.path)
  const right = clause.value
  switch (clause.op) {
    case 'EQ':
      return left === right
    case 'NEQ':
      return left !== right
    case 'IN':
      return Array.isArray(right) && (right as unknown[]).includes(left)
    case 'NOT_IN':
      return Array.isArray(right) && !(right as unknown[]).includes(left)
    case 'GT':
    case 'GTE':
    case 'LT':
    case 'LTE': {
      const l = asNumber(left)
      const r = asNumber(right)
      if (l === null || r === null) return false
      if (clause.op === 'GT') return l > r
      if (clause.op === 'GTE') return l >= r
      if (clause.op === 'LT') return l < r
      return l <= r
    }
    case 'CONTAINS':
      return typeof left === 'string' && typeof right === 'string' && left.includes(right)
    case 'NOT_CONTAINS':
      return !(typeof left === 'string' && typeof right === 'string' && left.includes(right))
    case 'EXISTS':
      return left !== undefined && left !== null && left !== ''
    case 'NOT_EXISTS':
      return left === undefined || left === null || left === ''
  }
}

export const evaluateCondition = (
  ctx: AutomationRunContext,
  condition: AutomationCondition | undefined,
): boolean => {
  if (!condition || condition.clauses.length === 0) return true
  if (condition.join === 'AND') return condition.clauses.every((clause) => evalClause(ctx, clause))
  return condition.clauses.some((clause) => evalClause(ctx, clause))
}

/** Does this trigger's config match the incoming event? Used for v2 multi-trigger match. */
export const triggerMatchesEvent = (
  trigger: AutomationTrigger,
  event: AutomationTriggerEvent,
): boolean => {
  if (trigger.type !== event.type) return false
  switch (event.type) {
    case 'NODE_CREATED': {
      const cfg = trigger.type === 'NODE_CREATED' ? trigger.config : ({} as Record<string, unknown>)
      if (cfg.boardKey && event.payload.boardKey && cfg.boardKey !== event.payload.boardKey) return false
      if (cfg.nodeKind && cfg.nodeKind !== 'ANY' && cfg.nodeKind !== event.payload.nodeKind) return false
      return true
    }
    case 'NODE_COMPLETED': {
      const cfg = trigger.type === 'NODE_COMPLETED' ? trigger.config : ({} as Record<string, unknown>)
      if (cfg.boardKey && event.payload.boardKey && cfg.boardKey !== event.payload.boardKey) return false
      if (cfg.nodeKind && cfg.nodeKind !== 'ANY' && cfg.nodeKind !== event.payload.nodeKind) return false
      return true
    }
    case 'FORM_SUBMITTED': {
      const cfg = trigger.type === 'FORM_SUBMITTED' ? trigger.config : ({} as Record<string, unknown>)
      if (cfg.formId && cfg.formId !== event.payload.formId) return false
      return true
    }
    case 'UPLOAD_COMPLETED': {
      const cfg = trigger.type === 'UPLOAD_COMPLETED' ? trigger.config : ({} as Record<string, unknown>)
      if (cfg.providerId && cfg.providerId !== 'ANY' && event.payload.providerId && cfg.providerId !== event.payload.providerId) return false
      return true
    }
    case 'FOCUS_BLOCK_COMPLETED': {
      const cfg = trigger.type === 'FOCUS_BLOCK_COMPLETED' ? trigger.config : ({} as { minMinutes?: number })
      const min = typeof cfg.minMinutes === 'number' ? cfg.minMinutes : Number(cfg.minMinutes ?? 0)
      if (min > 0 && (event.payload.minutes ?? 0) < min) return false
      return true
    }
    case 'SPIN_WON': {
      const cfg = trigger.type === 'SPIN_WON' ? trigger.config : ({} as { tier?: string })
      if (cfg.tier && cfg.tier !== 'ANY' && cfg.tier !== event.payload.tier) return false
      return true
    }
    case 'CHAT_MESSAGE_POSTED': {
      const cfg = trigger.type === 'CHAT_MESSAGE_POSTED' ? trigger.config : ({} as { channelId?: string; mentionsOnly?: boolean })
      if (cfg.channelId && cfg.channelId !== event.payload.channelId) return false
      if (cfg.mentionsOnly && !event.payload.hasMentions) return false
      return true
    }
    default:
      return true
  }
}

export const AVAILABLE_TRIGGER_TYPES: AutomationTriggerType[] = Object.values(TRIGGER_SCHEMAS).map((schema) => schema.type)
export const AVAILABLE_ACTION_TYPES: AutomationActionType[] = listActions({ surface: 'automate' }).map((action) => action.automate!.actionType)
